import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { produce } from 'immer'
import { CardStatus } from '../../../components/Layout/Card'
import {
  VoohBrandNetwork,
  VoohCampaignBasicValues,
  VoohCampaignForm,
  VoohCampaignFormSlice,
  VoohCreation,
  VoohMediaFormat,
  VoohMediaNode,
  VoohMediaTemplate,
} from './types'
import {
  CampaignAcceptanceStatus,
  CampaignStatus,
  VOOH_OFFER,
  VoohCampaign,
} from '../../../types/campaign'
import { BasicFormErrorsEmpty } from '../../Campaign/CampaignForm/BasicsForm/types'
import VariableUtils from '../../../utils/variable'
import { Company } from '../../../api/companies/types'
import { Agency } from '../../../types/agency'
import { User } from '../../../types/user'
import { SelectAsyncValue } from '../../../components/Form/SelectAsync'

export const defaultState: VoohCampaignFormSlice = {
  cardStatus: {
    basics: CardStatus.NONE,
    details: CardStatus.NONE,
    creations: CardStatus.NONE,
  },
  acceptanceStatus: CampaignAcceptanceStatus.not_sent,
  touched: false,
  publishable: false,
  loadingTemplates: false,
  status: CampaignStatus.SKETCH,
  note: '',
  form: {
    id: '',
    uuid: '',
    baseValues: {
      name: '',
      agency: null,
      clubMediaSource: null,
      advertiser: null,
      supervisors: [],
      startDate: '',
      endDate: '',
      briefName: '',
    },
    detailsValues: {
      organizationAttribute: null,
      offer: VOOH_OFFER.RUN_ON_NETWORK,
      mediaFormat: VoohMediaFormat.MOVE_TV,
      hourFrequency: 0,
      mediaDuration: 15,
    },
    selectedMediums: [],
    mediums: [],
    creations: [],
    mediaTemplates: [],
  },
  errors: BasicFormErrorsEmpty,
}

const initialState: VoohCampaignFormSlice = produce(defaultState, draft => draft)

const voohCampaignFormSlice = createSlice({
  name: 'campaignForm',
  initialState,
  reducers: {
    updateForm(state, action: PayloadAction<VoohCampaignForm>) {
      state.touched = true
      state.form = action.payload
    },

    setInitialAgency(state, action: PayloadAction<SelectAsyncValue<Agency>>) {
      state.form.baseValues.agency = action.payload
    },

    updateVoohCampaignNote(state, action: PayloadAction<VoohCampaign['note']>) {
      state.note = action.payload
    },

    setTouched(state) {
      state.touched = true
    },

    setVoohCampaign(_, action: PayloadAction<VoohCampaign>) {
      const voohCampaign = action.payload
      return {
        ...defaultState,
        note: voohCampaign.note,
        publishable: voohCampaign.publishable,
        status: voohCampaign.status,
        form: {
          ...defaultState.form,
          id: voohCampaign.id,
          uuid: voohCampaign.uuid,
          baseValues: {
            name: voohCampaign.name,
            agency: voohCampaign.agency
              ? VariableUtils.toSelectAsyncValue<Agency>({
                  value: voohCampaign.agency.name,
                  rawData: voohCampaign.agency,
                })
              : defaultState.form.baseValues.agency,
            clubMediaSource: voohCampaign.clubMediaSource
              ? VariableUtils.toSelectAsyncValue<VoohBrandNetwork>({
                  value: voohCampaign.clubMediaSource.name,
                  rawData: voohCampaign.clubMediaSource,
                })
              : defaultState.form.baseValues.clubMediaSource,
            advertiser: voohCampaign.advertiser
              ? VariableUtils.toSelectAsyncValue<Company>({
                  value: voohCampaign.advertiser.name,
                  rawData: voohCampaign.advertiser,
                })
              : defaultState.form.baseValues.advertiser,
            supervisors: voohCampaign
              ? voohCampaign.supervisors.map(user => {
                  return VariableUtils.toSelectAsyncValue<User>({
                    value: `${user.firstName} ${user.lastName}`,
                    rawData: user,
                  })
                })
              : defaultState.form.baseValues.supervisors,
            startDate: voohCampaign.startDate,
            endDate: voohCampaign.endDate,
            briefName: voohCampaign.briefName,
          },
          detailsValues: {
            organizationAttribute: voohCampaign.organizationAttribute,
            offer: voohCampaign.offer,
            mediaFormat: VoohMediaFormat.MOVE_TV,
            hourFrequency: voohCampaign.hourFrequency
              ? voohCampaign.hourFrequency
              : defaultState.form.detailsValues.hourFrequency,
            mediaDuration: voohCampaign.mediaDuration,
          },
          selectedMediums: voohCampaign.media,
          mediums: voohCampaign.mediaTree,
          creations: voohCampaign.creations,
        },
      }
    },

    setLaodingVoohMediaTemplates(state, action: PayloadAction<boolean>) {
      state.loadingTemplates = action.payload
    },

    setVoohMediaTemplates(state, action: PayloadAction<VoohMediaTemplate[]>) {
      state.form.mediaTemplates = action.payload
    },

    setVoohCampaignPublishable(state, action: PayloadAction<boolean>) {
      state.publishable = action.payload
    },

    updateCreationsDuration(state, action: PayloadAction<number | null>) {
      state.form.detailsValues.mediaDuration = action.payload
      state.form.creations = [
        ...state.form.creations.map(creation => {
          let creationDuration = creation.duration

          if (action.payload && !creation.file?.isVideo) {
            creationDuration = action.payload
          }

          return {
            ...creation,
            duration: creationDuration,
          }
        }),
      ]
    },
    setVoohMedia(state, action: PayloadAction<VoohMediaNode[]>) {
      state.form.mediums = action.payload
    },
    setSelectedVoohMedia(state, action: PayloadAction<VoohMediaNode[]>) {
      state.form.selectedMediums = action.payload
    },
    addCreations(state, action: PayloadAction<VoohCreation[]>) {
      state.form.creations = [...state.form.creations, ...action.payload]
    },
    resetCampaignForm() {
      return defaultState
    },
    setCardStatus(state, action: PayloadAction<Partial<VoohCampaignFormSlice['cardStatus']>>) {
      state.cardStatus = produce(state.cardStatus, draft => {
        return { ...draft, ...action.payload }
      })
    },
    setErrors(state, action: PayloadAction<VoohCampaignFormSlice['errors']>) {
      state.errors = action.payload
    },
    setError(state, action: PayloadAction<Partial<VoohCampaignBasicValues>>) {
      state.cardStatus = produce(state.cardStatus, draft => {
        return { ...draft, ...action.payload }
      })
    },
  },
})

export const voohCampaignFormActions = voohCampaignFormSlice.actions

export default voohCampaignFormSlice
